import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import type React from 'react';
import type { Ticket } from 'services/types/ticket';
import { META_BLUE, NAVY, RADIUS_ITEM, STATUS_GOOD, STATUS_WARN } from 'styles/tokens';

interface TicketStatusChipProps {
  status: Ticket['status'];
  size: 'small' | 'medium';
  /** When PENDING, true if the last message was sent by the current viewer's role (waiting on the other side). */
  lastMessageFromCurrentUser?: boolean;
}

const ERROR_RED = '#E7233B';

const getStatusColors = (
  status: Ticket['status'],
  lastMessageFromCurrentUser = false
): { bg: string; color: string } => {
  switch (status) {
    case 'PENDING':
      // I replied last → waiting on the other side → calm blue; otherwise something new for me → orange.
      return lastMessageFromCurrentUser
        ? { bg: alpha(META_BLUE, 0.14), color: META_BLUE }
        : { bg: alpha(STATUS_WARN, 0.14), color: STATUS_WARN };
    case 'IN_PROGRESS':
    case 'ANALYSIS_WITH_AI':
    case 'MANUAL_ANALYSIS_PENDING':
    case 'BLOCKED':
      return { bg: alpha(NAVY, 0.08), color: NAVY };
    case 'CLOSED':
      return { bg: alpha(STATUS_GOOD, 0.14), color: STATUS_GOOD };
    case 'FAILED':
      return { bg: alpha(ERROR_RED, 0.14), color: ERROR_RED };
    case 'DISMISSED':
      return { bg: alpha(NAVY, 0.06), color: `${NAVY}99` };
    default:
      return { bg: alpha(NAVY, 0.06), color: `${NAVY}99` };
  }
};

const getStatusLabel = (status: Ticket['status'], lastMessageFromCurrentUser = false) => {
  switch (status) {
    case 'PENDING':
      return lastMessageFromCurrentUser ? 'Pendiente - Esperando respuesta' : 'Pendiente - Nuevo mensaje';
    case 'IN_PROGRESS':
      return 'En Progreso';
    case 'ANALYSIS_WITH_AI':
      return 'Analizando automáticamente';
    case 'MANUAL_ANALYSIS_PENDING':
      return 'Análisis manual pendiente';
    case 'CLOSED':
      return 'Completado - Resuelto';
    case 'FAILED':
      return 'Fallido';
    case 'DISMISSED':
      return 'Descartado - No requiere acción';
    default:
      return status;
  }
};

const TicketStatusChip: React.FC<TicketStatusChipProps> = ({
  status,
  size = 'small',
  lastMessageFromCurrentUser = false,
}) => {
  const { bg, color } = getStatusColors(status, lastMessageFromCurrentUser);
  return (
    <Chip
      label={getStatusLabel(status, lastMessageFromCurrentUser)}
      size={size}
      sx={{
        bgcolor: bg,
        color,
        fontFamily: 'Jost, sans-serif',
        fontWeight: 600,
        fontSize: '0.65rem',
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        borderRadius: RADIUS_ITEM,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

export default TicketStatusChip;
